<template>
  <AppDialog
    class="manage-selections-dialog"
    :title="`${editing ? 'Edit' : 'New'} option`"
    :show="show"
    subtitle="This will update selection choices in all member profiles"
    append-to-body
    @close="close"
  >
    <form @submit.prevent="onFormSubmit">
      <div class="form-group">
        <label class="control-label">List position</label>
        <AppSelect
          class="max-w-30rem"
          :value="position"
          :options="selection.availablePositions"
          @input="newPosition = $event"
        />
      </div>

      <div
        v-if="editing"
        class="form-group"
      >
        <label class="control-label">Current label</label>
        <AppInput
          :value="selection.title"
          readonly
        />
      </div>

      <ValidationProvider
        v-slot="{ errors }"
        ref="titleField"
        name="title"
      >
        <div
          class="form-group"
          :class="{ 'has-error': errors[0] }"
        >
          <label class="control-label">{{ editing ? 'New label' : fieldName }}</label>
          <AppInput
            :value="newTitle"
            @input="newTitle = $event.trim()"
          />
          <AppDialogError :errors="errors" />
        </div>
      </ValidationProvider>

      <div
        v-if="selection.usage"
        class="dialog-warning orange d-flex align-items-center mb-5 mt-20 gap-5"
      >
        <i class="hi hi-warning text-orange mr-5" />
        This option is currently selected on {{ selection.usage }} member profiles
      </div>
      <div class="footer">
        <div class="d-flex mt-40">
          <AppButton
            v-if="editing"
            type="danger"
            @click="onDelete"
          >
            Delete
          </AppButton>
          <div class="ml-auto">
            <AppButton
              class="mr-15"
              type="default"
              @click="close"
            >
              Cancel
            </AppButton>

            <button
              class="btn btn-primary"
              type="submit"
              :disabled="disabled"
            >
              {{ label }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </AppDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'MemberProfileManageSelectionDialog',
  props: {
    field: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      default: true,
    },
    selection: {
      type: Object,
      default: () => {},
    },
    fieldSelections: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    newTitle: null,
    newPosition: null,
    submitting: false,
    newSelections: [],
  }),
  computed: {
    ...mapGetters(['organizationId']),
    label() {
      return this.submitting ? 'Saving...' : 'Save';
    },
    position() {
      return this.newPosition ? this.newPosition : this.selection.position;
    },
    editing() {
      return !!this.selection.title;
    },
    disabled() {
      return this.submitting || (this.editing ? !this.changed : !this.newTitle);
    },
    changed() {
      return !!(this.newTitle || this.newPosition);
    },
    hasErrors() {
      return this.$refs.titleField.errors.length;
    },
    fieldName() {
      return this.field.replace(/_/g, ' ');
    },
  },
  methods: {
    ...mapActions(['updateCustomField', 'loadCustomFields']),
    close() {
      this.$emit('close');
    },
    onFormSubmit() {
      const existsMessage = 'A selection with this title already exists.';

      this.newSelections = this.fieldSelections.map((item) => ({ ...item }));

      if (this.editing) {
        if (this.fieldSelections.some((obj) => obj.title === this.newTitle && obj.id !== this.selection.id)) {
          this.$refs.titleField.setErrors([existsMessage]);
        }

        const index = this.fieldSelections.findIndex((obj) => obj.id === this.selection.id);

        if (this.newTitle) {
          this.newSelections[index].title = this.newTitle;
        }

        if (this.position - 1 !== index) {
          const [updatedObj] = this.newSelections.splice(index, 1);

          this.newSelections.splice(this.position - 1, 0, updatedObj);
        }

        if (this.selection.usage && !this.hasErrors && this.newTitle) {
          this.$emit('open-confirm', { field: this.field, options: this.newSelections });
        } else {
          this.handleRequest();
        }
      } else {
        if (this.fieldSelections.some((obj) => obj.title === this.newTitle)) {
          this.$refs.titleField.setErrors([existsMessage]);
        }

        this.newSelections.splice(this.position - 1, 0, { title: this.newTitle });

        this.handleRequest();
      }
    },
    handleRequest() {
      if (!this.hasErrors) {
        this.submitting = true;
        this.updateCustomField({ id: this.organizationId, fieldKey: this.field, items: this.newSelections }).then(
          this.completeRequest,
        );
      }
    },
    completeRequest() {
      this.loadCustomFields();
      this.submitting = false;
      this.$appMessage('Options updated');
      this.close();
    },
    onDelete() {
      const updated = this.fieldSelections.filter((obj) => obj.id !== this.selection.id).map((item) => ({ ...item }));

      if (this.selection.usage) {
        this.$emit('open-confirm', { field: this.field, options: updated, delete: true });
      } else {
        this.newSelections = updated;
        this.handleRequest();
      }
    },
  },
};
</script>
